import React, { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import accountService from '../services/account.service';
import { useAuth } from '../contexts/AuthContext';

const AccountDeletionPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const initialReference = useMemo(
    () => new URLSearchParams(location.search).get('reference') || '',
    [location.search],
  );
  const [identifier, setIdentifier] = useState('');
  const [contact, setContact] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(initialReference ? 'Account deletion request recorded.' : '');
  const [referenceNumber, setReferenceNumber] = useState(initialReference);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSubmitting(true);
      setMessage('');
      const response = await accountService.requestPublicAccountDeletion({
        identifier,
        contact,
        reason,
      });
      setReferenceNumber(response.referenceNumber);
      setMessage(response.message);
      setIdentifier('');
      setContact('');
      setReason('');
    } catch (error: any) {
      const text = error?.response?.data?.message || 'Unable to record the deletion request right now.';
      setMessage(typeof text === 'string' ? text : 'Unable to record the deletion request right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_52%,_#f0fdfa_100%)]">
      <section className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(300px,1.05fr)] lg:items-start">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Privacy & Account</p>
            <h1 className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">Account deletion</h1>
            <p className="mt-4 text-sm leading-7 text-slate-600">
              LocalAlly users can request account and profile deletion from this page. Signed-in users can also delete from the in-app Privacy & Account screen.
            </p>
            <div className="mt-6 space-y-3 text-sm leading-6 text-slate-700">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Public student or tutor profile visibility is removed promptly after an in-app deletion request.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Records needed for fraud prevention, payment disputes, tax, legal, or safety review may be retained as required.
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                Support email: <a className="font-semibold text-indigo-700" href="mailto:support@localally.in">support@localally.in</a>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              {user ? (
                <Link
                  to="/account/privacy"
                  className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Open Privacy & Account
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                >
                  Login for in-app deletion
                </Link>
              )}
              <Link
                to="/privacy"
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Privacy Policy
              </Link>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-52px_rgba(15,23,42,0.45)] sm:p-8"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Deletion request form</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Request review</h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              Enter the phone, email, or username used on the account. A support reference is created after submission.
            </p>

            {message && (
              <div className="mt-5 rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm text-indigo-800">
                {message}
                {referenceNumber ? (
                  <span className="mt-2 block font-semibold text-indigo-950">Reference: {referenceNumber}</span>
                ) : null}
              </div>
            )}

            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">Registered phone, email, or username</label>
                <input
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  required
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Contact for follow-up</label>
                <input
                  value={contact}
                  onChange={(event) => setContact(event.target.value)}
                  placeholder="Optional alternate phone or email"
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Reason</label>
                <textarea
                  rows={4}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Optional"
                  className="mt-2 block w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-950 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-rose-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Submitting...' : 'Submit deletion request'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default AccountDeletionPage;
